import { createApiSlice } from 'helpers/createApiSlice';
import { put, select, take } from 'redux-saga/effects';
import { ApiStateData, MakerSelectors, UserData } from 'types';

import { configFetch } from './configFetch';
import { rolesFetch } from './generalSlices';
import { rfcsFetch } from './rfcsFetch';
import { userFetch } from './userFetch';

type Data = boolean;

interface ExtraSelectors extends MakerSelectors<ApiStateData<Data>> {
  getIsFailed: [
    typeof userFetch.selectors.getIsFailed,
    typeof configFetch.selectors.getIsFailed,
    (d: ApiStateData<Data>, user: boolean, config: boolean) => boolean
  ];
  getIsSuccess: [
    typeof userFetch.selectors.getIsSuccess,
    typeof configFetch.selectors.getIsSuccess,
    (d: ApiStateData<Data>, user: boolean, config: boolean) => boolean
  ];
  getIsNotStarted: [
    typeof userFetch.selectors.getIsNotStarted,
    typeof configFetch.selectors.getIsNotStarted,
    (d: ApiStateData<Data>, user: boolean, config: boolean) => boolean
  ];
  getIsBusy: [
    typeof userFetch.selectors.getIsBusy,
    typeof configFetch.selectors.getIsBusy,
    (d: ApiStateData<Data>, user: boolean, config: boolean) => boolean
  ];
  getHasData: [
    typeof userFetch.selectors.getData,
    typeof configFetch.selectors.getData,
    typeof rolesFetch.selectors.getData,
    typeof rfcsFetch.selectors.getData,
    (
      d: ApiStateData<Data>,
      user: ReturnType<typeof userFetch.selectors.getData>,
      config: ReturnType<typeof configFetch.selectors.getData>,
      role: ReturnType<typeof rolesFetch.selectors.getData>,
      rfc: ReturnType<typeof rfcsFetch.selectors.getData>
    ) => boolean
  ];
}

export const startUp = createApiSlice<Data, undefined, ExtraSelectors>({
  nestedPath: [],
  name: 'startUp',
  *tryGenerator() {
    yield put(userFetch.actions.call());
    yield take(userFetch.actions.completed.type);

    const userData: UserData = yield select(userFetch.selectors.getData);

    if (userData?.userType === 'ADMIN') {
      yield put(rolesFetch.actions.call());
      yield take(rolesFetch.actions.completed.type);
    } else {
      yield put(configFetch.actions.call());
      yield take(configFetch.actions.completed.type);

      yield put(rfcsFetch.actions.call());
      yield take(rfcsFetch.actions.completed.type);
    }

    return true;
  },
  extraSelectors: {
    getIsFailed: [
      userFetch.selectors.getIsFailed,
      configFetch.selectors.getIsFailed,
      (_, user, config) => !!user || !!config,
    ],
    getIsSuccess: [
      userFetch.selectors.getIsSuccess,
      configFetch.selectors.getIsSuccess,
      (_, user, config) => user && config,
    ],
    getIsNotStarted: [
      userFetch.selectors.getIsNotStarted,
      configFetch.selectors.getIsNotStarted,
      (_, user, config) => !!user && !!config,
    ],
    getIsBusy: [
      userFetch.selectors.getIsBusy,
      configFetch.selectors.getIsBusy,
      (_, user, config) => !!user || !!config,
    ],
    getHasData: [
      userFetch.selectors.getData,
      configFetch.selectors.getData,
      rolesFetch.selectors.getData,
      rfcsFetch.selectors.getData,
      (_, user, config, role, rfcs) => {
        if (!user) return false;

        const { userType } = user;

        return (userType === 'ADMIN' && !!role) || (userType === 'USER' && !!config && !!rfcs);
      },
    ],
  },
});
